import { makeGetRouteForLastSelectedTab } from './factories'
import { httpGet, httpPost, httpPatchMerge, httpDelete } from './http'
import type { TEntityId } from '~/types/models'
import type {
  ICompany,
  ICompanyOverview,
  ICompanyLastSelectedState,
  ICompanyContact,
} from '~/types/company/models'
import type { IOverviewForPost } from '~/types/order/models'
import type { ILocation } from '~/types/location/models'
import { CompanyContact } from '~/cynqueModules/dataObjects/CompanyContact'
import { CompanyMasterService } from '~/cynqueModules/dataObjects/CompanyMasterService'
import { Location } from '~/cynqueModules/dataObjects/Location'

// should be imported from service/companies
interface GetCompaniesResponse {
  items: ICompany[]
}

function assertGetCompaniesResponse(
  obj: any
): asserts obj is GetCompaniesResponse {
  if (!('items' in obj)) {
    console.dir(obj)
    throw new Error('obj does not implement interface GetCompaniesResponse.')
  }
}

const apiEntityPath = '/api/companies'

export async function getCompanies({ apiPath = apiEntityPath } = {}) {
  const callingMn = getCompanies.name
  return httpGet<ICompany[]>({ callingMn, apiPath })
}

export async function getCompany(id: string) {
  const callingMn = getCompany.name
  const apiPath = `${apiEntityPath}/${id}`
  return httpGet<ICompany>({ callingMn, apiPath })
}

export async function getAccountCompanyForCompany(id: string) {
  const callingMn = getAccountCompanyForCompany.name
  const apiPath = `${apiEntityPath}/${id}`
  return httpGet<ICompany>({ callingMn, apiPath })
}

export async function getCompanyOverview(id: string) {
  const callingMn = getCompanyOverview.name
  const apiPath = `${apiEntityPath}/${id}/overview`
  return httpGet<ICompanyOverview>({
    callingMn,
    apiPath,
  })
}

export async function getLastSelectedStateForCompany(id: TEntityId) {
  const callingMn = getLastSelectedStateForCompany.name
  const apiPath = `${apiEntityPath}/${id}/lastselectedstate`
  return httpGet<ICompany>({ callingMn, apiPath })
}

export const getRouteForLastSelectedTab =
  makeGetRouteForLastSelectedTab<ICompanyLastSelectedState>(
    apiEntityPath,
    'Companies-Overview',
    'Operations'
  )

// export async function getRouteForLastSelectedTab(id: string) {
//   const callingMn = getRouteForLastSelectedTab.name
//   const apiPath = `${apiEntityPath}/${id}/lastselectedstate`
//   const data = await httpGet<ICompanyLastSelectedState>({
//     callingMn,
//     apiPath,
//   })
//   const tab = data?.selectedScreenTopTabValue ?? 'Operations'
//   console.debug(`getRouteForLastSelectedTab`, tab)
//   return `Companies-Overview-${tab}-id`
// }

export async function getRouteAsPathForLastSelectedTab(id: string) {
  const data = await getRouteForLastSelectedTab(id)
  const path = '/' + data.replaceAll('-', '/').replace('/id', `/${id}`)
  console.debug(`getRouteAsPathForLastSelectedTab`, path)
  return path
}

export function getRouteForOnCreated(id: string) {
  return { name: 'Companies-Overview-Operations-id', params: { id } }
}

export async function getContactsForCompanyIdPreProcessed(id: TEntityId) {
  const callingMn = getContactsForCompanyIdPreProcessed.name
  const apiPath = `${apiEntityPath}/${id}/contacts`
  const rawContacts = await httpGet<ICompanyContact[]>({
    callingMn,
    apiPath,
  })
  return rawContacts.map((rawContact) =>
    Object.assign(rawContact, CompanyContact(rawContact))
  )
}

export async function getLocationsForCompanyId(id: TEntityId) {
  const callingMn = getLocationsForCompanyId.name
  const apiPath = `${apiEntityPath}/${id}/locations`
  const rawLocations = await httpGet<ILocation[]>({
    callingMn,
    apiPath,
  })
  return rawLocations.map((rawLocation) =>
    Object.assign(rawLocation, Location(rawLocation))
  )
}

export async function getCompanyMasterServicesForCompanyId(id: TEntityId) {
  const companyOverview = await getCompanyOverview(id)
  const rawCompanyMasterServices = companyOverview?.companyMasterServices ?? []
  return rawCompanyMasterServices.map((rawCompanyMasterService) =>
    CompanyMasterService(rawCompanyMasterService)
  )
}

export async function createCompany(entity: ICompany) {
  const callingMn = createCompany.name
  const apiPath = apiEntityPath
  return httpPost<ICompany>({ callingMn, apiPath, entity })
}

export async function updateCompany(
  entityData: Partial<ICompany>,
  id: TEntityId
) {
  const callingMn = updateCompany.name
  const apiPath = `${apiEntityPath}/${id}`
  type TPartialICompany = Partial<ICompany>
  return httpPatchMerge({
    callingMn,
    apiPath,
    entityData,
  })
}

export async function addToCompanyOverview(entity: IOverviewForPost) {
  const callingMn = addToCompanyOverview.name
  const apiPath = `${apiEntityPath}/overview`
  return httpPost<IOverviewForPost>({
    callingMn,
    apiPath,
    entity,
    asyncOptions: { doAsync: true },
  })
}

export async function deleteCompany(entityData: ICompany) {
  const callingMn = deleteCompany.name
  const apiPath = apiEntityPath
  return httpDelete<ICompany>({
    callingMn,
    apiPath,
    entityData,
  })
}
